/**
 * Presents one Vault item in grid layout without changing item semantics.
 *
 * Grid and row views share the same selection, provenance, version, and action
 * contracts. Keeping this component presentational prevents layout choice from
 * creating a second permission or mutation path.
 */
import { useState } from 'react'
import { Download, Info, LockKeyhole } from 'lucide-react'

import { Button, Checkbox, cn } from '@/components/ui'
import { VaultItemIcon } from './VaultItemIcon'
import type { ItemResponse } from '../types'
import {
  canEdit,
  formatDate,
  formatSize,
  getDragPayload,
  hasDragPayload,
  hasFilePayload,
  kindLabel,
  setDragPayload,
  type VaultDragPayload,
} from '../lib/format'
import { VaultItemMenu } from './VaultItemMenu'
import { VaultVersionBadge } from './VaultVersionBadge'
import { SourceProviderBadge } from '@/components/SourceProviderBadge'

interface VaultGridCardProps {
  entry: ItemResponse
  index: number
  trashView: boolean
  selected: boolean
  checked: boolean
  showSourceBadge: boolean
  onOpen: () => void
  onDetails: () => void
  onDownload: () => void
  onShare: () => void
  onRename: () => void
  onMove: () => void
  onCopy: () => void
  onNewVersion: () => void
  onVersionHistory: () => void
  onTrash: () => void
  onRestore: () => void
  onCheckedChange: (checked: boolean) => void
  onDropItem: (payload: VaultDragPayload, targetFolderId: string) => void
  onDropFiles: (files: File[], targetFolderId: string) => void
}

export function VaultGridCard({
  entry,
  index,
  trashView,
  selected,
  checked,
  showSourceBadge,
  onOpen,
  onDetails,
  onDownload,
  onDropItem,
  onDropFiles,
  onCheckedChange,
  ...actions
}: VaultGridCardProps) {
  const [dropTarget, setDropTarget] = useState(false)
  const { item } = entry
  const droppable = item.kind === 'folder' && !trashView && canEdit(entry)
  const acceptDrop = (event: React.DragEvent) =>
    droppable && (hasFilePayload(event) || hasDragPayload(event))

  return (
    <div
      role="listitem"
      draggable={!trashView && canEdit(entry)}
      onDragStart={(event) => setDragPayload(event, {
        itemId: item.itemId,
        revision: item.revision,
        name: item.name,
        kind: item.kind,
      })}
      onDragOver={(event) => {
        if (!acceptDrop(event)) return
        event.preventDefault()
        setDropTarget(true)
      }}
      onDragLeave={() => setDropTarget(false)}
      onDrop={(event) => {
        if (!droppable) return
        event.preventDefault()
        setDropTarget(false)
        const files = Array.from(event.dataTransfer.files)
        if (files.length) return onDropFiles(files, item.itemId)
        const payload = getDragPayload(event)
        if (payload && payload.itemId !== item.itemId) onDropItem(payload, item.itemId)
      }}
      className={cn(
        'group relative min-w-0 rounded-lg border bg-card p-3',
        'transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md',
        'animate-in fade-in slide-in-from-bottom-1 fill-mode-both',
        selected && 'border-primary bg-primary/5 ring-1 ring-primary',
        dropTarget && 'bg-primary/5 ring-2 ring-primary',
      )}
      style={{ animationDelay: `${Math.min(index, 15) * 25}ms` }}
    >
      {!trashView && (
        <span className="absolute left-2 top-2 z-10 rounded bg-background/90 p-1 shadow-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => onCheckedChange(value === true)}
            aria-label={`Select ${item.name}`}
          />
        </span>
      )}
      <div className="w-full min-w-0 text-left">
        <button type="button" className="block w-full text-left" onClick={onOpen}>
        <VaultItemIcon
          kind={item.kind}
          name={item.name}
        linked={item.linked || item.readOnly}
          size="lg"
          className={cn(
            'mb-3 transition-transform duration-200 ease-out group-hover:scale-105',
            !trashView && 'mt-6',
            dropTarget && 'scale-110',
          )}
        />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <button type="button" className="min-w-0 truncate text-left text-sm font-medium" onClick={onOpen}>
            {item.name}
          </button>
          {showSourceBadge && <SourceProviderBadge provider={item.sourceConnector} />}
          {item.metadata.read_only_badge === 'true' && (
            <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 text-[10px] font-semibold text-slate-500">
              <LockKeyhole className="h-2.5 w-2.5" /> Read-only
            </span>
          )}
          {!trashView && item.kind === 'file'
            && item.currentVersionNumber !== undefined && (
            <VaultVersionBadge
              name={item.name}
              versionNumber={item.currentVersionNumber}
              onOpen={actions.onVersionHistory}
            />
          )}
        </div>
        <button type="button" className="block w-full text-left" onClick={onOpen}>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {kindLabel(entry)} · {formatDate(item.updatedAt)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {item.kind === 'file' ? formatSize(item.sizeBytes) : '—'}
          </p>
        </button>
      </div>
      <div
        className="absolute right-2 top-2 flex items-center rounded-md bg-background/90 shadow-sm backdrop-blur-sm transition-all duration-150 ease-out sm:-translate-y-1 sm:opacity-0 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100 sm:group-hover:translate-y-0 sm:group-hover:opacity-100"
        onClick={(event) => event.stopPropagation()}
      >
        {!trashView && (
          <>
            {item.kind === 'file' && (
              <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90" aria-label={`Download ${item.name}`} onClick={onDownload}>
                <Download className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90" aria-label={`Details for ${item.name}`} onClick={onDetails}>
              <Info className="h-4 w-4" />
            </Button>
          </>
        )}
        <VaultItemMenu
          entry={entry}
          trashView={trashView}
          onDownload={onDownload}
          {...actions}
        />
      </div>
    </div>
  )
}
